import graphql from "babel-plugin-relay/macro";
import { useFragment } from "react-relay";

import { JoinBooking } from "@/screens/JoinBooking/types";
import { DropdownSelectOption } from "@whoosh/design-system/src/components/controls/DropdownSelect";
import { useGetClubCurrency } from "@whoosh/features/src/ClubCurrency";
import { ConvertCentsToDollars } from "@whoosh/utilities/src/Currency";

import { useGetActivityManagementPlayerRateOptions_Booking$key } from "./__generated__/useGetActivityManagementPlayerRateOptions_Booking.graphql";

export const useGetActivityManagementPlayerRateOptions = ({
  bookingRef,
  round,
}: {
  bookingRef: useGetActivityManagementPlayerRateOptions_Booking$key | null;
  round: JoinBooking.CustomerRound | null;
}) => {
  const { currencySymbol } = useGetClubCurrency();
  const booking = useFragment(
    useGetActivityManagementPlayerRateOptionsString,
    bookingRef
  );

  const slot = booking?.startSlot;
  const playerRates = slot?.durationPlayerRates;

  const isGolfRound = round?.type === "Golf";
  const currentRate = isGolfRound ? round?.rate : null;

  const isCurrentRateLocked = currentRate?.isLocked ?? false;

  const singleOption: DropdownSelectOption<string> = {
    value: currentRate?.id ?? "",
    label: currentRate?.name || "",
    subLabel: `${currencySymbol}${ConvertCentsToDollars(
      currentRate?.eighteenHolePrice ?? 0
    )}`,
  };

  const dropdownOptions: DropdownSelectOption<string>[] =
    playerRates?.flatMap((rate) => {
      if (!rate?.rate?.id) return [];
      const priceInCents = rate.totalDurationPrice;
      const priceDisplay = ConvertCentsToDollars(priceInCents ?? 0);
      return {
        value: rate.rate.id,
        label: rate.rate.name || "",
        subLabel: `${currencySymbol}${priceDisplay}`,
      };
    }) ?? [];

  return isCurrentRateLocked ? [singleOption] : dropdownOptions;
};

const useGetActivityManagementPlayerRateOptionsString = graphql`
  fragment useGetActivityManagementPlayerRateOptions_Booking on Booking {
    startSlot {
      id
      ... on TimeSlot {
        durationPlayerRates: playerRates(
          highestPrecedentOnly: false
          type: CUSTOMER
        ) {
          rate {
            id
            name
          }
          totalDurationPrice(duration: $duration, playerCount: $playerCount)
        }
      }
    }
  }
`;
